import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { ThemeConfig, ThemeName } from "@/types/theme";
import { getTheme } from "@/config/themes";

interface ThemeContextType {
  theme: ThemeConfig;
  themeName: ThemeName;
  setTheme: (themeName: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const THEME_STORAGE_KEY = "app-theme";

// Funções helper para gerenciar tema no sessionStorage (temporário apenas para a sessão atual)
export const getThemeFromStorage = (): ThemeName => {
  if (typeof window === "undefined") return "default";
  const stored = sessionStorage.getItem(THEME_STORAGE_KEY);
  if (stored && ["default", "siga-facil", "zapay"].includes(stored)) {
    return stored as ThemeName;
  }
  return "default";
};

export const setThemeInStorage = (themeName: ThemeName): void => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(THEME_STORAGE_KEY, themeName);
  // Dispara evento customizado para atualizar o ThemeContext
  window.dispatchEvent(new CustomEvent("theme-changed"));
};

export const clearThemeFromStorage = (): void => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(THEME_STORAGE_KEY);
  // Dispara evento customizado para atualizar o ThemeContext
  window.dispatchEvent(new CustomEvent("theme-changed"));
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Sempre lê do sessionStorage primeiro, padrão é 'default'
  const [themeName, setThemeName] = useState<ThemeName>(getThemeFromStorage());
  const [theme, setThemeConfig] = useState<ThemeConfig>(
    getTheme(getThemeFromStorage()),
  );

  // Monitora mudanças no sessionStorage (para quando outras abas mudarem o tema)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        const newTheme = e.newValue as ThemeName;
        if (["default", "siga-facil", "zapay"].includes(newTheme)) {
          setThemeName(newTheme);
          setThemeConfig(getTheme(newTheme));
        }
      }
    };

    // Evento customizado para mudanças na mesma janela
    const handleThemeChange = () => {
      const storedTheme = getThemeFromStorage();
      if (storedTheme !== themeName) {
        setThemeName(storedTheme);
        setThemeConfig(getTheme(storedTheme));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("theme-changed", handleThemeChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("theme-changed", handleThemeChange);
    };
  }, [themeName]);

  // Verifica o sessionStorage periodicamente para garantir sincronização
  useEffect(() => {
    const checkStorage = () => {
      const storedTheme = getThemeFromStorage();
      if (storedTheme !== themeName) {
        setThemeName(storedTheme);
        setThemeConfig(getTheme(storedTheme));
      }
    };

    // Verifica imediatamente
    checkStorage();

    // Verifica a cada 200ms para garantir sincronização rápida
    const intervalId = setInterval(checkStorage, 200);

    return () => {
      clearInterval(intervalId);
    };
  }, [themeName]);

  // Aplica as variáveis CSS quando o tema muda
  useEffect(() => {
    const root = document.documentElement;

    // Cores
    root.style.setProperty("--color-primary", theme.colors.primary);
    root.style.setProperty("--color-primary-light", theme.colors.primaryLight);
    if (theme.colors.secondary) {
      root.style.setProperty("--color-secondary", theme.colors.secondary);
    }
    if (theme.colors.tertiary) {
      root.style.setProperty("--color-tertiary", theme.colors.tertiary);
    }
    root.style.setProperty("--color-background", theme.colors.background);
    root.style.setProperty("--color-foreground", theme.colors.foreground);
    root.style.setProperty("--color-text", theme.colors.text);
    if (theme.colors.textSecondary) {
      root.style.setProperty(
        "--color-text-secondary",
        theme.colors.textSecondary,
      );
    }
    root.style.setProperty("--color-border", theme.colors.border);
    if (theme.colors.error) {
      root.style.setProperty("--color-error", theme.colors.error);
    }
    if (theme.colors.success) {
      root.style.setProperty("--color-success", theme.colors.success);
    }
    if (theme.colors.disabled) {
      root.style.setProperty("--color-disabled", theme.colors.disabled);
    }
    if (theme.colors.disabledText) {
      root.style.setProperty(
        "--color-disabled-text",
        theme.colors.disabledText,
      );
    }

    // Fontes
    root.style.setProperty("--font-primary", theme.fonts.primary);
    if (theme.fonts.secondary) {
      root.style.setProperty("--font-secondary", theme.fonts.secondary);
    }

    // Espaçamentos
    root.style.setProperty("--spacing-base", theme.spacing.base);
    root.style.setProperty("--spacing-small", theme.spacing.small);
    root.style.setProperty("--spacing-medium", theme.spacing.medium);
    root.style.setProperty("--spacing-large", theme.spacing.large);
    root.style.setProperty("--spacing-xlarge", theme.spacing.xlarge);

    // Bordas
    root.style.setProperty("--radius-small", theme.borderRadius.small);
    root.style.setProperty("--radius-medium", theme.borderRadius.medium);
    root.style.setProperty("--radius-large", theme.borderRadius.large);
    root.style.setProperty("--radius-xlarge", theme.borderRadius.xlarge);

    // Adiciona classe ao body para facilitar seletores CSS
    document.body.setAttribute("data-theme", themeName);
  }, [theme, themeName]);

  const setTheme = (newThemeName: ThemeName) => {
    setThemeInStorage(newThemeName);
    setThemeName(newThemeName);
    setThemeConfig(getTheme(newThemeName));
  };

  return (
    <ThemeContext.Provider value={{ theme, themeName, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }
  return context;
};
